import {
  alpha,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuProps,
} from "@mui/material";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import { TriageBarProps } from "~/components/TriageBar/TriageBar";
import { Link } from "react-router-dom";
import { useConversationStarter } from "~/hooks/useConversationStarter/useConversationStarter";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { AttachIcon, DoneIcon, SlackIcon } from "~/icons";
import {
  EditOff as EditOffIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useCanCreateTicketForCase } from "~/lib/iam/actions/mondoo/integrations/tickets/TicketSystemIntegration/CreateTicketForCase";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import { useScope } from "~/hooks/useScope";

interface Props extends MenuProps {
  anchorId: string;
  menuId: string;
  type?: "small-table" | "action-cell" | "triage-bar";
  availableRemediations?: TriageBarProps["availableRemediations"];
  conversation?: TriageBarProps["conversation"];
}

export function TakeActionMenu({
  anchorId,
  menuId,
  type,
  availableRemediations,
  conversation,
  sx,
  open,
  anchorEl,
  ...menuProps
}: Props) {
  const { startConversation } = useConversationStarter();
  const { isFeatureEnabled } = useFeatureFlags();

  const { activeScope, spaceScope } = useScope();
  const canCreateTicket = useCanCreateTicketForCase(spaceScope);
  const canSetException = useCanApplyExceptionMutation(activeScope);

  const isCreateTicketActive = availableRemediations?.ticket.active;
  const isSecurityPipelinePRActive =
    availableRemediations?.securityPipeline.active;
  const isSetExceptionActive = availableRemediations?.exception.active;
  const shouldNavigateToSpaceLevel =
    availableRemediations?.exception.shouldNavigateToSpaceLevel;
  const exception = availableRemediations?.exception;

  const width = (
    typeof anchorEl === "function" ? anchorEl() : anchorEl
  )?.getBoundingClientRect().width;

  const anchorOrigin: MenuProps["anchorOrigin"] = {
    vertical: "bottom",
    horizontal: "right",
  };
  const transformOrigin: MenuProps["transformOrigin"] = {
    vertical: "top",
    horizontal: "right",
  };

  const showStartConvo =
    isFeatureEnabled("Slack Start Convo") && conversation !== undefined;

  return (
    <Menu
      slotProps={{
        list: {
          id: menuId,
          "aria-labelledby": anchorId,
          "aria-expanded": open ? "true" : "false",
        },
      }}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={[
        {
          ".MuiMenu-paper": {
            border: "1px solid",
            borderColor: (theme) => theme.palette.primary.main,
            borderTopLeftRadius: type === "triage-bar" ? 0 : 4,
            borderTopRightRadius: 0,
            minWidth: width ? width + "px" : undefined,
            mt: "-1px",
          },
          ".MuiMenuItem-root": {
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...menuProps}
    >
      {isSecurityPipelinePRActive && (
        <MenuItem
          onClick={(e) => {
            menuProps.onClose?.(e, "backdropClick");
            availableRemediations?.securityPipeline.onClick?.();
          }}
          disabled={availableRemediations?.securityPipeline.disabled}
        >
          <ListItemIcon>
            <DoneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Fix Finding</ListItemText>
        </MenuItem>
      )}
      {isCreateTicketActive && (
        <MenuItem
          onClick={(e) => {
            menuProps.onClose?.(e, "backdropClick");
            availableRemediations?.ticket.onClick?.();
          }}
          disabled={availableRemediations?.ticket.disabled || !canCreateTicket}
        >
          <ListItemIcon>
            <AttachIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Ticket</ListItemText>
        </MenuItem>
      )}
      {isSetExceptionActive && !exception?.hasException && (
        <MenuItem
          onClick={(e) => {
            menuProps.onClose?.(e, "backdropClick");
            exception?.onClick?.();
          }}
          disabled={exception?.disabled || !canSetException}
        >
          <ListItemIcon>
            <DoNotDisturbOnOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Set Exception</ListItemText>
        </MenuItem>
      )}
      {isSetExceptionActive && exception?.hasException && (
        <>
          {shouldNavigateToSpaceLevel ? (
            <MenuItem
              component={Link}
              to={exception.linkToException || "#"}
              className="triage-button transparent"
              disabled={exception?.disabled}
            >
              <ListItemIcon>
                <OpenInNewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View Exception</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem
              className="triage-button transparent"
              onClick={(e) => {
                menuProps.onClose?.(e, "backdropClick");
                exception?.onClick?.();
              }}
              disabled={exception?.disabled || !canSetException}
            >
              <ListItemIcon>
                <EditOffIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Remove Exception and Enable</ListItemText>
            </MenuItem>
          )}
        </>
      )}
      {showStartConvo && [
        <Divider />,
        <MenuItem onClick={() => startConversation(conversation)}>
          <ListItemIcon>
            <SlackIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Start Conversation</ListItemText>
        </MenuItem>,
      ]}
    </Menu>
  );
}
